// ✅ Final fix for loading bug when refreshing cart page with context fallback

import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Empty, Alert, notification } from 'antd';
import { ShoppingCart, ArrowLeft, Download } from 'lucide-react';
import CartContext from '../context/CartContext';
import CartPropertyCard from './CartPropertyCard';
import CartTotal from './CartTotal';
import DateTimeModal from './DateTimeModal';
import userRoleUtils from '../utils/userRoleUtils';
import eventBus from '../utils/eventBus';
import { exportCartToExcel } from '../utils/cartExcelExport';
import { resolveCartProperties } from '../utils/cartPropertyUtils';


const Cart = ({ isAdminOrImpersonating, onUpdateCart: parentUpdateCart, cart: propCart, orderSchedule: propOrderSchedule, propertyData, ordersData, properties: propProperties, removeFromCart: parentRemoveFromCart }) => {
  const navigate = useNavigate();
  const contextValue = useContext(CartContext);
  const hasContext = contextValue !== undefined;

  const cart = propCart || (hasContext ? contextValue.cart : {});
  const properties = resolveCartProperties(
    propProperties,
    hasContext ? contextValue.cartProperties : []
  );
  const orderSchedule = propOrderSchedule || (hasContext ? contextValue.orderSchedule : {});
  const isLoading = hasContext ? contextValue.isLoading : false;
  const error = hasContext ? contextValue.error : null; const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [tempOrderSchedule, setTempOrderSchedule] = useState(null);
  const [localOrderSchedule, setLocalOrderSchedule] = useState({});
  const [showVat, setShowVat] = useState(true);
  const updateCartItem = (propertyId, items) => {

    if (parentUpdateCart) {
      parentUpdateCart(propertyId, items);
    }

    if (hasContext && contextValue.updateCartItem) {
      contextValue.updateCartItem(propertyId, items);
    }
       setTimeout(() => {
      if (hasContext && contextValue.refreshCart) {
        contextValue.refreshCart();
      }
    }, 100);
  };
  const removeFromCart = (propertyId) => {
    if (parentRemoveFromCart) return parentRemoveFromCart(propertyId);
    else if (hasContext && contextValue.removeFromCart) return contextValue.removeFromCart(propertyId);
  };

  const refreshCart = () => {
    if (hasContext && contextValue.refreshCart) {
      contextValue.refreshCart();
    }
    try {
      const savedSchedule = localStorage.getItem('dateIsSet');
      if (savedSchedule) {
        const parsed = JSON.parse(savedSchedule);
        setLocalOrderSchedule(parsed);
      }
    } catch (e) {
      console.error("Error refreshing from localStorage", e);
    }
  };

 

  if (!isLoading && Object.keys(cart).length > 0 && properties.length === 0) {
    return (
      <div className="text-center text-red-500 mt-10">
        ⚠️ Failed to load property data. Please refresh the page.
        <button
          onClick={() => refreshCart()}
          className="ml-4 underline text-blue-600 hover:text-blue-800"
        >Try Again</button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-4 sm:p-6 h-64 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Loading your cart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Alert
          message="Error Loading Cart"
          description={error}
          type="error"
          showIcon
          action={
            <button
              onClick={() => window.location.reload()}
              className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
            >Refresh Page</button>
          }
        />
      </div>
    );
  }

  if (!cart || Object.keys(cart).length === 0) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <div className="bg-white rounded-lg shadow-lg p-5 sm:p-8 text-center">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Your cart is empty"
          />
          <button
            onClick={() => navigate(userRoleUtils.isAdmin() ? '/admin/properties' : '/properties')}
            className="mt-6 bg-golden-navy text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-colors inline-flex items-center"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Return to Properties
          </button>
        </div>
      </div>
    );
  }



  const handleCalendarClick = (propertyId, scheduleData) => {
    if (propertyId) {
      setSelectedPropertyId(propertyId);
      setTempOrderSchedule(scheduleData);
      setIsDateModalOpen(true);
    }
     if (hasContext && contextValue.setIsModalOpen) {
      contextValue.setIsModalOpen(false);
          eventBus.emit('modal:close');

    }
    else {

      contextValue.setIsModalOpen(true);
      eventBus.emit('modal:open');
     }
    
  };

  const handleDateTimeModalSave = (updatedDateTime) => {
    setLocalOrderSchedule(prev => ({
      ...prev,
      [selectedPropertyId]: {
        arrival_time: updatedDateTime.arrival_time,
        completion_time: updatedDateTime.completion_time,
        order_date: updatedDateTime.order_date
      }
    }));

    try {
      const currentSchedule = JSON.parse(localStorage.getItem('dateIsSet') || '{}');
      const updatedSchedule = {
        ...currentSchedule,
        [selectedPropertyId]: {
          arrival_time: updatedDateTime.arrival_time,
          completion_time: updatedDateTime.completion_time,
          order_date: updatedDateTime.order_date
        }
      };
      localStorage.setItem('dateIsSet', JSON.stringify(updatedSchedule));
      eventBus.emit('schedule:updated', updatedSchedule);
    } catch (e) {
      console.error("Error saving to localStorage", e);
    }

    if (hasContext && contextValue.updateSchedule) {
      contextValue.updateSchedule(selectedPropertyId, {
        arrival_time: updatedDateTime.arrival_time,
        completion_time: updatedDateTime.completion_time,
        order_date: updatedDateTime.order_date
      });
    }

    setTimeout(() => {
      if (hasContext && contextValue.refreshCart) {
        contextValue.refreshCart();
      }
    }, 100);

    setIsDateModalOpen(false);
     eventBus.emit('datetime:updated', {
      propertyId: selectedPropertyId,
      displayDate: new Date(updatedDateTime.order_date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }),
      timeSlot: `${updatedDateTime.arrival_time} - ${updatedDateTime.completion_time}`
    });
    
  };

  const effectiveOrderSchedule = { ...localOrderSchedule, ...orderSchedule };

  const handleDownloadExcel = () => {
    const exported = exportCartToExcel({ cart, properties });

    if (!exported) {
      notification.warning({
        message: 'No cart data',
        description: 'Add at least one property to the cart before exporting.',
      });
      return;
    }

    notification.success({
      message: 'Excel downloaded',
      description: 'The cart details file includes totals with and without VAT.',
    });
  };
  const canUseCartAdminTools = userRoleUtils.canViewCartVatToggle();

  return (
    <div className="container mx-auto p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center">
          <ShoppingCart className="mr-2 h-6 w-6" />
          Your Cart ({Object.keys(cart).length} {Object.keys(cart).length === 1 ? 'item' : 'items'})
        </h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {canUseCartAdminTools && (
            <div className="inline-flex rounded-lg border border-golden-navy bg-white p-1">
              <button
                type="button"
                onClick={() => setShowVat(true)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${showVat ? 'bg-golden-navy text-white' : 'text-golden-navy'}`}
              >
                VAT On
              </button>
              <button
                type="button"
                onClick={() => setShowVat(false)}
                className={`px-3 py-1 text-sm font-medium rounded-md ${!showVat ? 'bg-golden-navy text-white' : 'text-golden-navy'}`}
              >
                VAT Off
              </button>
            </div>
          )}
          {canUseCartAdminTools && (
            <button
              onClick={handleDownloadExcel}
              className="inline-flex items-center justify-center rounded-lg bg-golden-navy px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-opacity-90"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Excel
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">
        <div className="min-w-0 flex-grow">
          {Object.entries(cart).map(([propertyId, items]) => (
            <CartPropertyCard
              key={propertyId}
               cart={cart}
              propertyId={propertyId}
              cartItems={items}
              propertyData={properties}
              orderSchedule={effectiveOrderSchedule}
              removeFromCart={removeFromCart}
              handleCalendarClick={handleCalendarClick}
              onUpdateCart={updateCartItem}
              showVat={showVat}
            />
          ))}
        </div>

        <div className="w-full lg:w-96 lg:flex-shrink-0">
          <CartTotal cart={cart} showVat={showVat} />
        </div>
      </div>

      <DateTimeModal
        isOpen={isDateModalOpen}
        onSave={handleDateTimeModalSave}
        onClose={() => setIsDateModalOpen(false)}
        propertyId={selectedPropertyId}
        initialData={tempOrderSchedule}
      />
    </div>
  );
};

export default Cart;
